<template>
  <v-card-text>
    <v-data-table
      :items="audiences"
      :headers="headers"
      :loading="loading"
      :search="search"
      :items-per-page="-1"
      :footer-props="{ 'items-per-page-options': [25, 50, 100, -1] }"
      sort-by="customers"
      sort-desc
      class="row-pointer"
      mobile-breakpoint="0"
      @click:row="rowClick"
    >
      <template #[`item.typeName`]="{ item }">
        <v-chip
          small
          outlined
        >
          <v-icon
            small
            left
          >
            {{ item.targetType === $enums.AUDIENCE.TYPE.INDUSTRY ? 'mdi-domain' : 'mdi-tag-outline' }}
          </v-icon>
          {{ item.typeName }}
        </v-chip>
      </template>

      <template #[`item.customers`]="{ item }">
        {{ item.customers | separator }}
      </template>

      <template #[`item.printRevenue`]="{ item }">
        <span :class="item.printRevenue ? 'success--text' : 'grey--text'">
          {{ item.printRevenue | currencyShort }}
        </span>
      </template>

      <template #[`item.subscriptionRevenue`]="{ item }">
        <span :class="item.subscriptionRevenue ? '' : 'grey--text'">
          {{ item.subscriptionRevenue | currencyShort }}
        </span>
      </template>

      <template #[`item.hasForPage`]="{ item }">
        <v-icon
          small
          :color="item.hasForPage ? 'success' : 'error'"
          :title="item.hasForPage ? 'Landing page is live' : 'No live landing page'"
        >
          {{ item.hasForPage ? 'mdi-check-circle' : 'mdi-alert-circle-outline' }}
        </v-icon>
      </template>

      <template #[`item.planStatus`]="{ item }">
        <v-chip
          v-if="item.planStatus"
          x-small
          :color="planChipFor(item).color"
          :title="planChipFor(item).title"
          dark
        >
          {{ planChipFor(item).label }}
        </v-chip>
        <span
          v-else
          class="text-caption grey--text"
        >No plan</span>
        <v-chip
          v-if="item.pendingItems"
          x-small
          outlined
          class="ml-1"
          :title="item.pendingItems + ' suggestion(s) awaiting a decision'"
        >
          {{ item.pendingItems }}
        </v-chip>
      </template>

      <template #[`item.scheduledItems`]="{ item }">
        <span :class="item.scheduledItems ? '' : 'error--text'">
          {{ item.scheduledItems || 'Nothing planned' }}
        </span>
      </template>
    </v-data-table>
  </v-card-text>
</template>

<script>
import { mapGetters } from 'vuex';

export default {
  name: 'AudienceList',
  props: {
    // 0 = all, otherwise an AUDIENCE.TYPE value.
    typeFilter: {
      type: Number,
      default: 0
    },
    search: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      loading: false
    };
  },
  computed: {
    ...mapGetters({ allAudiences: 'audiences/list' }),
    audiences() {
      if (!this.typeFilter) return this.allAudiences;
      return this.allAudiences.filter((a) => a.targetType === this.typeFilter);
    },
    headers() {
      return [
        { text: 'Audience', value: 'name' },
        { text: 'Type', value: 'typeName' },
        { text: 'Customers', value: 'customers', align: 'end' },
        { text: 'Print spend', value: 'printRevenue', align: 'end' },
        { text: 'Subscription spend', value: 'subscriptionRevenue', align: 'end' },
        { text: 'Landing page', value: 'hasForPage', align: 'center', sortable: false },
        { text: 'Plan', value: 'planStatus' },
        { text: 'Planned (12 wks)', value: 'scheduledItems', align: 'end' }
      ];
    }
  },
  mounted() {
    this.loadData();
  },
  methods: {
    loadData() {
      this.loading = true;
      this.$store
        .dispatch('audiences/getList')
        .catch((error) => this.logError(error))
        .finally(() => {
          this.loading = false;
        });
    },
    planStatusFor(status) {
      return this.$enums.AUDIENCE.PLAN_STATUS[status] || { label: '—', color: 'grey' };
    },
    /**
     * The one chip in the Plan column. Runway wins over the stored status when it has something to say:
     * a plan that has run out still says "Active" for ever, and its pending count drops to zero, so the row
     * that most needs an admin is the one that looks most finished.
     */
    planChipFor(item) {
      const health = this.$enums.AUDIENCE.PLAN_HEALTH[item.planHealth];
      if (!health) return { ...this.planStatusFor(item.planStatus), title: 'Plan status' };

      const last = item.lastBookedSendDate
        ? this.$options.filters.normalDate(item.lastBookedSendDate)
        : null;
      return {
        ...health,
        title: last
          ? `Last booked send ${last}, with nothing behind it`
          : 'Nothing booked behind the last planned send'
      };
    },
    rowClick(item) {
      this.$router.push(`/marketing/audiences/${item.targetType}-${item.targetId}`);
    }
  }
};
</script>
